/**
 * 雛形のサブステップが持つ承認フロー設定 (spec AC-WFT-010e / 010f) の
 * 選べるモード・既定値・wire との変換。
 *
 * `allowedApprovalModes` は backend の `AllowedApprovalModes`
 * (go/internal/backend/domain/entity/workflow/sub_step_approval_setting.go) のミラー。
 * backend 側の表を変えた時は本ファイルも同期する。
 */
import { create } from '@bufbuild/protobuf'
import { ApprovalMode } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { ApprovalFlowStageSchema } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import {
  LinkedPageKind,
  type SubStepApprovalSetting,
  SubStepApprovalSettingSchema,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import { lacksApprovers as lacksStageApprovers } from '~/components/approval/stage'
import type { SubStepApprovalSettingFormValues } from '~/pages/AdminWorkflowTemplates/models/schema'

/** 雛形のサブステップが持てる承認モード (spec AC-WFT-010e の 3 値)。 */
export type SubStepApprovalMode =
  | ApprovalMode.NO_APPROVAL
  | ApprovalMode.APPROVAL
  | ApprovalMode.AUTO

const ALL_MODES: readonly SubStepApprovalMode[] = [
  ApprovalMode.NO_APPROVAL,
  ApprovalMode.APPROVAL,
  ApprovalMode.AUTO,
]

/**
 * リンク先画面ごとに選べる承認モード (表示順)。 空配列は承認フロー設定を
 * 雛形に持たないサブステップ (スポット入札 / 時間前入札。 approval-flow 画面が SoT)。
 */
export function allowedApprovalModes(
  linkedPageKind: LinkedPageKind | undefined,
): readonly SubStepApprovalMode[] {
  switch (linkedPageKind) {
    case LinkedPageKind.BIDDING_SPOT:
    case LinkedPageKind.BIDDING_INTRADAY:
      return []
    default:
      return ALL_MODES
  }
}

/** spec AC-WFT-010e の既定値「承認なし」。 */
export const DEFAULT_APPROVAL_SETTING: SubStepApprovalSettingFormValues = {
  mode: ApprovalMode.NO_APPROVAL,
  stages: [],
}

/**
 * リンク先を変えた時の設定。 入札では設定を外し (undefined)、 選べないモードと
 * 未設定は「承認なし」 にする。 入札から戻しても古い段階を復活させない。
 * 需要想定の自動設定を公開していない時も、新しいリンク先では自動を引き継がない。
 */
export function resolveApprovalSetting(
  linkedPageKind: LinkedPageKind | undefined,
  setting: SubStepApprovalSettingFormValues | undefined,
  automationAvailable: boolean,
): SubStepApprovalSettingFormValues | undefined {
  const modes = allowedApprovalModes(linkedPageKind)
  if (modes.length === 0) return undefined
  if (
    setting === undefined ||
    !(modes as readonly ApprovalMode[]).includes(setting.mode) ||
    (setting.mode === ApprovalMode.AUTO &&
      linkedPageKind === LinkedPageKind.DEMAND_FORECAST_DAILY &&
      !automationAvailable)
  ) {
    return DEFAULT_APPROVAL_SETTING
  }
  return setting
}

/**
 * Get 応答の設定を form の値にする。 入札は持たない (undefined)。 入札以外で
 * 設定が無ければ spec の既定値「承認なし」。
 */
export function approvalSettingFromProto(
  linkedPageKind: LinkedPageKind | undefined,
  setting: SubStepApprovalSetting | undefined,
): SubStepApprovalSettingFormValues | undefined {
  if (allowedApprovalModes(linkedPageKind).length === 0) return undefined
  if (setting === undefined) return DEFAULT_APPROVAL_SETTING
  return {
    mode: setting.mode,
    stages: setting.stages.map((s) => ({
      strategy: s.strategy,
      approverMemberIds: [...s.approverMemberIds],
    })),
  }
}

/**
 * 保存要求に載せる設定。 form の値は入札で undefined、 それ以外で必ず値を持つ
 * (schema が検証する)。 入札以外は必ず送る: 送らないと backend が InvalidArgument で
 * 拒否する。 段階は要承認の時だけ送り、 stage_order は 1..N。
 */
export function approvalSettingToProto(
  setting: SubStepApprovalSettingFormValues | undefined,
): SubStepApprovalSetting | undefined {
  if (setting === undefined) return undefined
  return create(SubStepApprovalSettingSchema, {
    mode: setting.mode,
    stages:
      setting.mode === ApprovalMode.APPROVAL
        ? setting.stages.map((s, i) =>
            create(ApprovalFlowStageSchema, {
              stageOrder: i + 1,
              strategy: s.strategy,
              approverMemberIds: [...s.approverMemberIds],
            }),
          )
        : [],
  })
}

/**
 * 同じ承認者が 2 つの承認段階に入っていれば、 その段番号 (1 起点) を返す。
 * 2 段承認は二重チェックが目的で、 同じ人が両段にいると 1 人で全段を通せるため
 * 保存させない (mock-app `ApprovalFlowConfig.FindDuplicateApproverAcrossStages`、
 * backend `SubStepApprovalSetting.ValidateForSave`)。
 */
export function findApproverAcrossStages(
  setting: SubStepApprovalSettingFormValues,
): { firstStage: number; secondStage: number } | undefined {
  const stageOf = new Map<string, number>()
  for (const [i, stage] of setting.stages.entries()) {
    for (const id of stage.approverMemberIds) {
      const first = stageOf.get(id)
      if (first !== undefined) {
        return { firstStage: first + 1, secondStage: i + 1 }
      }
      stageOf.set(id, i)
    }
  }
  return undefined
}

/** 要承認なのに承認者を決めきっていない (段階 0、 または承認者 0 人の段階がある)。 */
export function lacksApprovers(
  setting: SubStepApprovalSettingFormValues,
): boolean {
  return lacksStageApprovers(
    setting.mode === ApprovalMode.APPROVAL,
    setting.stages,
  )
}
